/**
 * Delimited-text clipboard interchange (Requirement 12.8).
 *
 * Three jobs, and only one of them is new code:
 *
 * - {@link joinDelimited} turns a table into the `text/plain` flavour a copy puts
 *   on the system clipboard. Tab-separated by default, which is what every other
 *   spreadsheet reads.
 * - {@link sniffDelimiter} decides which delimiter a pasted string uses, over the
 *   four candidates the design names: tab, comma, semicolon, pipe. The rule is the
 *   design's: *the delimiter with the most consistent field count per line wins*.
 *   This is the new part.
 * - {@link parseDelimitedText} splits the text into a rectangular table.
 *
 * **There is one CSV state machine, and it lives in `core/csv`.** Quoted fields,
 * doubled `""` escapes, embedded delimiters, and embedded line breaks are all
 * handled by {@link parseCsvText}; the quoting rule on the way out is
 * {@link encodeCsvField}'s. A clipboard paste differs from a file import in exactly
 * two ways — it arrives as one already-decoded string, and nobody says which
 * delimiter it used — so this module supplies the sniffing and the padding and
 * borrows everything else. Two parsers would be two opinions about what a quote
 * means.
 *
 * **Malformed records are kept, not skipped.** The CSV_Parser reports a record whose
 * field count differs from the first record's as a `BadRow` and drops it from the row
 * list, which is right for an import report (Requirement 10.9) and wrong for a paste:
 * a ragged clipboard block is still a block the user expects to land. So the two
 * lists are merged back into source order and short rows are padded, which is what
 * gives the paste rectangular dimensions.
 */

import { encodeCsvField, parseCsvText, resolveDialect, type CsvDialect } from '../csv/index.js';

/** Delimiters a paste is sniffed against, in preference order. */
export const CLIPBOARD_DELIMITERS: readonly string[] = ['\t', ',', ';', '|'];

/** The delimiter a copy writes. */
export const DEFAULT_CLIPBOARD_DELIMITER = '\t';

/** The dialect the codec is driven with: text mode, no header, LF terminators. */
function clipboardDialect(delimiter: string): CsvDialect {
  return resolveDialect({ delimiter, hasHeader: false, newline: '\n' });
}

/** True when `field` cannot be written unquoted under `delimiter`. */
export function needsQuoting(field: string, delimiter: string): boolean {
  return encodeCsvField(field, clipboardDialect(delimiter)) !== field;
}

/**
 * Joins a table into delimited text, quoting only the fields that need it.
 *
 * Rows are separated by, not terminated with, a newline: the clipboard's
 * `text/plain` flavour is what the user sees pasted into a text editor, and a
 * trailing blank line there is noise. {@link parseDelimitedText} tolerates either.
 */
export function joinDelimited(
  table: readonly (readonly string[])[],
  delimiter: string = DEFAULT_CLIPBOARD_DELIMITER,
): string {
  const dialect = clipboardDialect(delimiter);
  return table
    .map((row) => row.map((field) => encodeCsvField(field, dialect)).join(delimiter))
    .join('\n');
}

/**
 * Every record in `text`, in source order, malformed ones included.
 *
 * The CSV_Parser numbers records from 1 and counts a record with embedded newlines
 * once, so the two lists it returns reassemble by record number without ambiguity.
 */
function parseRecords(text: string, delimiter: string): string[][] {
  if (text.length === 0) return [];
  const { rows, badRows } = parseCsvText(text, clipboardDialect(delimiter));
  const byRecord = new Map<number, string[]>();
  for (const row of rows) byRecord.set(row.row, row.fields);
  for (const bad of badRows) byRecord.set(bad.row, bad.fields);
  return [...byRecord.keys()].sort((a, b) => a - b).map((no) => byRecord.get(no) as string[]);
}

/** Splits one line into fields, unwrapping quotes and undoubling `""`. */
export function splitLine(line: string, delimiter: string): string[] {
  return parseRecords(line, delimiter)[0] ?? [''];
}

/** True when a record is what an empty line parses to. */
function isBlankRecord(record: readonly string[]): boolean {
  return record.length === 1 && record[0] === '';
}

/** The most frequent field count, ties going to the larger count. */
function modalCount(counts: readonly number[]): number {
  const tally = new Map<number, number>();
  for (const count of counts) tally.set(count, (tally.get(count) ?? 0) + 1);
  let best = 0;
  let bestSeen = 0;
  for (const [count, seen] of tally) {
    if (seen > bestSeen || (seen === bestSeen && count > best)) {
      best = count;
      bestSeen = seen;
    }
  }
  return best;
}

/**
 * The delimiter `text` most likely uses, or `null` when no candidate splits it
 * (a single-column paste).
 *
 * The design's rule, made precise: a candidate qualifies when its *modal* field count
 * across lines is greater than one, and its score is the fraction of lines that hit
 * that modal count — "the most consistent field count per line". The highest score
 * wins.
 *
 * Ties go to the earlier candidate in {@link CLIPBOARD_DELIMITERS}, tab first, and
 * that ordering is doing real work: text copied from another spreadsheet is
 * tab-separated and its cells routinely contain commas, so a rule that preferred the
 * *finer* split would shred every such paste. A ragged table stays detectable because
 * the score is a fraction rather than an all-or-nothing test — `a,b,c` over `d,e` is
 * still comma-separated.
 */
export function sniffDelimiter(text: string): string | null {
  if (text.length === 0) return null;
  let best: { delimiter: string; score: number } | null = null;
  for (const delimiter of CLIPBOARD_DELIMITERS) {
    const counts = parseRecords(text, delimiter)
      .filter((record) => !isBlankRecord(record))
      .map((record) => record.length);
    if (counts.length === 0) continue;
    const modal = modalCount(counts);
    if (modal <= 1) continue;
    const score = counts.filter((count) => count === modal).length / counts.length;
    if (best === null || score > best.score) best = { delimiter, score };
  }
  return best === null ? null : best.delimiter;
}

/**
 * Distributes delimited text across cells (Requirement 12.8).
 *
 * Trailing blank records are dropped — every clipboard writer ends its payload with a
 * newline, and a phantom last row would overwrite a cell the user can see is not
 * part of what they copied. Short records are padded so the result is rectangular,
 * which is what a paste needs in order to have dimensions at all.
 */
export function parseDelimitedText(text: string, delimiter?: string): string[][] {
  if (text.length === 0) return [];
  const chosen = delimiter ?? sniffDelimiter(text) ?? DEFAULT_CLIPBOARD_DELIMITER;
  const records = parseRecords(text, chosen);
  while (records.length > 1 && isBlankRecord(records[records.length - 1] as string[])) records.pop();
  const cols = records.reduce((max, record) => Math.max(max, record.length), 0);
  return records.map((record) =>
    record.length === cols
      ? record
      : [...record, ...new Array<string>(cols - record.length).fill('')],
  );
}
